import { useCallback, useMemo, useState } from 'react';
import { dayjs } from 'util/shared/date';
import { shortIdGenerator } from 'util/optionsCalculator';
import {
  IVAdjustmentSettings,
  OC_DEFAULT_CALL_SLOPE_MULT,
  OC_DEFAULT_LEVEL_SHIFT,
  OC_DEFAULT_PUT_SLOPE_MULT,
  OptionsCalculatorCanvasConfig,
  Position as PositionType,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import { HeatmapMetric } from 'components/optionsCalculator/pnlChart/heatmapMetrics';
import { OptionsCalculatorState } from './useOptionsCalculator';

const DEFAULT_ENABLED_HEATMAP_METRICS: HeatmapMetric[] = ['pnl'];

/**
 * Config-driven counterpart to `useOptionsCalculator` (which is URL-synced,
 * a single global instance per page). A canvas widget instead keeps its
 * position (legs, shares, IV settings, base date) on its own component
 * config, so any number of calculators can coexist on one workspace without
 * fighting over the same query params.
 *
 * `sym` is intentionally NOT tracked in local state here — it's fully
 * controlled via `config.sym`/`onSymChange`, the same convention every
 * other single-symbol canvas component uses, so it stays compatible with
 * symbol-sharing groups.
 */
const useOptionsCalculatorCanvasState = (
  sym: string,
  onSymChange: (newSym: string) => void,
  config: OptionsCalculatorCanvasConfig,
  onConfigChange: (patch: Partial<OptionsCalculatorCanvasConfig>) => void,
): OptionsCalculatorState => {
  const [localState, setLocalState] = useState<
    Required<
      Pick<
        OptionsCalculatorCanvasConfig,
        'baseDate' | 'legs' | 'shares' | 'ivSettings' | 'enabledHeatmapMetrics'
      >
    >
  >(() => ({
    baseDate: config.baseDate ?? dayjs().valueOf(),
    legs: config.legs ?? [],
    shares: config.shares ?? [],
    ivSettings: config.ivSettings ?? [],
    enabledHeatmapMetrics:
      config.enabledHeatmapMetrics ?? DEFAULT_ENABLED_HEATMAP_METRICS,
  }));

  const patchState = useCallback(
    (patch: Partial<OptionsCalculatorCanvasConfig>) => {
      setLocalState((prev) => ({ ...prev, ...patch }));
      onConfigChange(patch);
    },
    [onConfigChange],
  );

  const addLeg = useCallback(
    (leg: PositionLeg) => {
      const legWithId = { ...leg, leg_id: shortIdGenerator() };
      patchState({ legs: [...localState.legs, legWithId] });
    },
    [localState.legs, patchState],
  );

  const removeLeg = useCallback(
    (legId: number) => {
      patchState({
        legs: localState.legs.filter((leg) => leg.leg_id !== legId),
      });
    },
    [localState.legs, patchState],
  );

  const removeLegsBulk = useCallback(
    (legIds: number[]) => {
      patchState({
        legs: localState.legs.filter((leg) => !legIds.includes(leg.leg_id)),
      });
    },
    [localState.legs, patchState],
  );

  const syncIVSettingsWithLegs = useCallback(
    (legs: PositionLeg[]): IVAdjustmentSettings[] => {
      const expirations = [
        ...new Set(
          legs
            .map((leg) => leg.expiry)
            .filter((expiration): expiration is number => expiration != null),
        ),
      ];

      return expirations.map((expiration) => {
        const existing = localState.ivSettings.find(
          (settings) => settings.expiry === expiration,
        );
        return (existing ?? {
          sym,
          expiry: expiration,
          level_shift: OC_DEFAULT_LEVEL_SHIFT,
          call_slope_mult: OC_DEFAULT_CALL_SLOPE_MULT,
          put_slope_mult: OC_DEFAULT_PUT_SLOPE_MULT,
        }) as IVAdjustmentSettings;
      });
    },
    [localState.ivSettings, sym],
  );

  const updateLeg = useCallback(
    (legId: number, updatedFields: Partial<PositionLeg>) => {
      const newLegs = localState.legs.map((leg) =>
        leg.leg_id === legId ? { ...leg, ...updatedFields } : leg,
      );
      patchState({
        legs: newLegs,
        ivSettings: syncIVSettingsWithLegs(newLegs),
      });
    },
    [localState.legs, patchState, syncIVSettingsWithLegs],
  );

  const updateLegsBulk = useCallback(
    (legIds: number[], updatedFields: Partial<PositionLeg>) => {
      patchState({
        legs: localState.legs.map((leg) =>
          legIds.includes(leg.leg_id) ? { ...leg, ...updatedFields } : leg,
        ),
      });
    },
    [localState.legs, patchState],
  );

  // Applies each leg's patch atomically - looping updateLeg drops all but the last call.
  const updateLegsIndividually = useCallback(
    (updates: { legId: number; fields: Partial<PositionLeg> }[]) => {
      const fieldsByLegId = new Map(updates.map((u) => [u.legId, u.fields]));
      const newLegs = localState.legs.map((leg) => {
        return { ...leg, ...(fieldsByLegId.get(leg.leg_id) ?? {}) };
      });
      patchState({
        legs: newLegs,
        ivSettings: syncIVSettingsWithLegs(newLegs),
      });
    },
    [localState.legs, patchState, syncIVSettingsWithLegs],
  );

  const clearLegs = useCallback(() => {
    patchState({ legs: [] });
  }, [patchState]);

  const updateShares = useCallback(
    (shares: PositionShares[]) => {
      patchState({ shares });
    },
    [patchState],
  );

  const updateIVSettings = useCallback(
    (ivSettings: Partial<IVAdjustmentSettings>) => {
      const filtered = localState.ivSettings.filter(
        (settings) => settings.expiry !== ivSettings.expiry,
      );
      patchState({
        ivSettings: [...filtered, ivSettings as IVAdjustmentSettings],
      });
    },
    [localState.ivSettings, patchState],
  );

  const updateBaseDate = useCallback(
    (newBaseDate: number) => {
      patchState({ baseDate: newBaseDate });
    },
    [patchState],
  );

  const setEnabledHeatmapMetrics = useCallback(
    (metrics: HeatmapMetric[]) => {
      patchState({ enabledHeatmapMetrics: metrics });
    },
    [patchState],
  );

  const setActivePosition = useCallback(
    (position: PositionType) => {
      if (position == null) {
        return;
      }
      // Only broadcast a sym change when it actually changed — this call
      // fans out through the symbol-sharing group machinery separately
      // from `patchState`, so skipping it when redundant means a strategy
      // click for the currently-active symbol makes exactly one config
      // update instead of two racing ones.
      if (position.sym !== sym) {
        onSymChange(position.sym);
      }
      patchState({
        legs: position.option_legs ?? [],
        shares: position.underlying_shares ?? [],
        ivSettings: position.iv_adjustments ?? [],
      });
    },
    [sym, onSymChange, patchState],
  );

  return useMemo(
    () => ({
      sym,
      baseDate: localState.baseDate,
      legs: localState.legs,
      shares: localState.shares,
      ivSettings: localState.ivSettings,
      handleSymChange: onSymChange,
      updateIVSettings,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      updateLegsIndividually,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
      setActivePosition,
      enabledHeatmapMetrics: localState.enabledHeatmapMetrics,
      setEnabledHeatmapMetrics,
    }),
    [
      sym,
      localState,
      onSymChange,
      updateIVSettings,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      updateLegsIndividually,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
      setEnabledHeatmapMetrics,
      setActivePosition,
    ],
  );
};

export default useOptionsCalculatorCanvasState;
